import React, { FunctionComponent, Suspense, useRef, useState, useEffect, useCallback, useMemo } from 'react';

import { find, isUndefined } from 'lodash';
import { DateTime } from 'luxon';
import { Button, Nav, Tab, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { FeatureFlagId, isFeatureFlagEnabled } from 'admin/feature-flags/FeatureFlags';
import {
    countNotificationsHistories,
    markAllNotificationHistoriesAsViewed,
    updateNotificationHistoryStatuses,
    updateNotificationHistoryStatus,
    getNotificationLink,
    FieldName,
    removeNotificationHistory,
} from 'admin/notifications/Notifications';
import {
    GroupNotificationHistory,
    NotificationHistoryStatus,
    NotificationHistory,
    NotificationChannel,
    NotificationType,
    DesktopAlert,
} from 'admin/notifications/Notifications.types';
import { useProfile, isAuthorized } from 'auth/Auth';
import { getDesktopAlertLoader } from 'common/notification-tray/desktop-alert-registry/DesktopAlertRegistry';
import { DesktopAlertManager } from 'common/notification-tray/desktop-notification-alert/DesktopAlertManager';
import { NotificationTrayItem } from 'common/notification-tray/notification-tray-item/NotificationTrayItem';
import { NotificationTrayItemProAct } from 'common/notification-tray/notification-tray-item/NotificationTrayItemProAct';
import { useNotificationsHook, NOTIFICATION_COUNT } from 'common/notification-tray/useNotificationsHook';
import { OutsideClick } from 'common/outside-click/OutsideClick';
import { Overlay } from 'core/overlay/Overlay';
import { route } from 'core/util/Routing';
import { SearchRequest, Operator } from 'core/util/Search';

import 'common/notification-tray/alert-registrations/ProActComponentRegistry';
import { NotificationTrayContext } from './NotificationTrayContext';

import 'common/notification-tray/NotificationTray.scss';

const getObjectIDs = (notification: NotificationHistory): string[] => {
    if (notification.objectIDs?.length) {
        return notification.objectIDs;
    }
    if (notification.objectID) {
        return [notification.objectID];
    }
    return [];
};

const toDesktopAlert = (notification: NotificationHistory): DesktopAlert => ({
    id: notification.id,
    eventType: notification.eventType,
    payload: notification.payload,
    impactAnalysis: notification.subject,
    objectIDs: getObjectIDs(notification),
    notificationHistoryIDs: [notification.id],
    tenantID: notification.tenantID,
    createdDate: notification.sentDate,
    userID: notification.userID,
    agencyID: notification.agencyID,
    metadata: notification.metadata,
});

type NotificationTrayProps = {
    notificationCount: number;
    isModalOpenRef?: React.MutableRefObject<boolean>;
};

export const NotificationTray: FunctionComponent<NotificationTrayProps> = ({ notificationCount, isModalOpenRef }) => {
    const { t } = useTranslation('admin/notifications');
    const [{ profile }] = useProfile();
    const [currentStart, setCurrentStart] = useState(0);
    const [currentUnreadStart, setCurrentUnreadStart] = useState(0);
    const [currentRemindMeLaterStart, setCurrentRemindMeLaterStart] = useState(0);
    const [unreadNotificationCount, setUnreadNotificationCount] = useState<number>(0);
    const [remindMeLaterNotificationCount, setRemindMeLaterNotificationCount] = useState<number>(0);
    const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
    const [hasMoreUnreadNotifications, setHasMoreUnreadNotifications] = useState(true);
    const [hasMoreRemindMeLaterNotifications, setHasMoreRemindMeLaterNotifications] = useState(true);
    const [notificationHistories, setNotificationHistories] = useState<NotificationHistory[]>([]);
    const [unreadNotificationHistories, setUnreadNotificationHistories] = useState<NotificationHistory[]>([]);
    const [remindMeLaterNotificationHistories, setRemindMeLaterNotificationHistories] = useState<NotificationHistory[]>(
        [],
    );
    const [groupedNotificationHistory, setGroupedNotificationHistory] = useState<GroupNotificationHistory>({});
    const [groupedUnreadNotificationHistory, setGroupedUnreadNotificationHistory] = useState<GroupNotificationHistory>(
        {},
    );
    const [groupedRemindMeLaterNotificationHistory, setGroupedRemindMeLaterNotificationHistory] =
        useState<GroupNotificationHistory>({});
    const [isProActFeatureFlagEnabled, setIsProActFeatureFlagEnabled] = useState<boolean>(false);
    const [activeAlertNotification, setActiveAlertNotification] = useState<NotificationHistory | null>(null);
    const [currentTab, setCurrentTab] = useState<string>('all');

    const { fetchNotificationHistories } = useNotificationsHook();

    const ref = useRef<HTMLDivElement | null>(null);
    const lazyComponentCache = useRef<Map<string, React.LazyExoticComponent<any>>>(new Map());

    useEffect(() => {
        if (isModalOpenRef) {
            // eslint-disable-next-line no-param-reassign
            isModalOpenRef.current = activeAlertNotification !== null;
        }
    }, [activeAlertNotification, isModalOpenRef]);

    const handleNotificationCount = (id: string, status: NotificationHistoryStatus): void => {
        const updateStatus =
            status === NotificationHistoryStatus.UnRead
                ? NotificationHistoryStatus.Read
                : NotificationHistoryStatus.UnRead;

        (async (): Promise<void> => {
            try {
                await trackPromise(
                    updateNotificationHistoryStatus(profile?.tenant?.id, { id, status: updateStatus }),
                    'notifications-history',
                );
                const selectedNotification = find(notificationHistories, { id });

                if (selectedNotification) {
                    selectedNotification.status = updateStatus;
                    setUnreadNotificationCount((prev) =>
                        status === NotificationHistoryStatus.UnRead ? prev - 1 : prev + 1,
                    );
                }
            } catch {
                // Silently handle
            }
        })();
    };

    const handleStatePath = (notification: NotificationHistory): void => {
        if (profile && !profile.isImpersonated) {
            updateNotificationHistoryStatus(profile?.tenant?.id, {
                id: notification.id,
                status: NotificationHistoryStatus.Read,
            });
        }

        (async (): Promise<void> => {
            try {
                const statePathResponse = await trackPromise(
                    getNotificationLink(profile?.tenant?.id, notification.notificationID, {
                        notificationObjectId: notification.objectID,
                        notificationType: notification.type,
                        eventType: notification.eventType,
                    }),
                    'notifications-history',
                );

                window.location.href = route(statePathResponse.url);
            } catch {
                // Silently handle
            }
        })();
    };

    const handleNotificationAction = useCallback(
        (notification: NotificationHistory): void => {
            if (notification.status === NotificationHistoryStatus.UnRead) {
                updateNotificationHistoryStatus(profile?.tenant?.id, {
                    id: notification.id,
                    status: NotificationHistoryStatus.Read,
                });
            }
            setActiveAlertNotification(notification);
        },
        [profile],
    );

    const handleCloseActiveAlert = useCallback((): void => {
        setActiveAlertNotification(null);
    }, []);

    const getGroupedDates = (diffDays: number | undefined): string => {
        if (diffDays) {
            const roundedDiffDays = Math.round(diffDays);

            if (diffDays < 1) return 'TODAY';
            if (roundedDiffDays < 2) return 'YESTERDAY';
            if (roundedDiffDays < 7) return 'THIS_WEEK';
            if (roundedDiffDays < 11) return 'LAST_WEEK';
        }
        return 'EARLIER';
    };

    const getTrayIcon = (notificationType: NotificationType): string => {
        switch (notificationType) {
            case NotificationType.InboxMessage:
                return 'icon-inbox';
            case NotificationType.Mention:
            case NotificationType.ScheduledLocationPost:
            case NotificationType.SocialPost:
            case NotificationType.SocialPostApproval:
            case NotificationType.SocialReply:
            case NotificationType.SocialNonAPIPost:
                return 'icon-comments';
            case NotificationType.Survey1:
            case NotificationType.SurveyResponseApproval:
            case NotificationType.SurveyResult:
                return 'icon-clipboard-list-check';
            case NotificationType.Ticket:
            case NotificationType.TicketResponse:
                return 'icon-ticket';
            default:
                return 'icon-star';
        }
    };

    const groupNotificationHistory = (notificationHistory: NotificationHistory[]): GroupNotificationHistory => {
        return notificationHistory.reduce((acc, notification) => {
            const duration = DateTime.fromMillis(Date.now()).diff(DateTime.fromMillis(notification.sentDate), ['days']);
            const groupedByDate = getGroupedDates(duration.as('days'));

            const updatedNotification = {
                ...notification,
                icon: getTrayIcon(notification.type),
            };

            return {
                ...acc,
                [groupedByDate]: [...(acc[groupedByDate] || []), updatedNotification],
            };
        }, {} as GroupNotificationHistory);
    };

    const handleRemoveNotification = async (notification: NotificationHistory): Promise<void> => {
        try {
            await trackPromise(
                removeNotificationHistory(profile?.tenant?.id, notification.id),
                'notifications-history',
            );

            if (currentTab === 'all') {
                handleClearNotifications();
            } else if (currentTab === 'unread') {
                handleClearUnreadNotifications();
            } else if (currentTab === 'remindMeLater') {
                handleClearRemindMeLaterNotifications();
            }

            loadNotificationHistories(currentTab);
            fetchNotificationsCount();
            fetchNotificationsCount(NotificationHistoryStatus.UnRead);

            if (isProActFeatureFlagEnabled) {
                fetchNotificationsCount(NotificationHistoryStatus.RemindMeLater);
            }
        } catch {
            console.log('Unable to delete');
        }
    };

    const loadNotificationHistories = async (tab: string, start?: number, count?: number): Promise<void> => {
        const notifications = await fetchNotificationHistories(tab, start, count);

        if (tab === 'unread') {
            if (notifications.length > 0) {
                const total = !isUndefined(start) ? unreadNotificationHistories.concat(notifications) : notifications;

                setUnreadNotificationHistories(total);
                setGroupedUnreadNotificationHistory(groupNotificationHistory(total));
                setCurrentUnreadStart(currentUnreadStart + NOTIFICATION_COUNT);
            }
            if (notifications.length < NOTIFICATION_COUNT) {
                setHasMoreUnreadNotifications(false);
            }
        } else if (tab === 'remindMeLater') {
            if (notifications.length > 0) {
                const total = !isUndefined(start)
                    ? remindMeLaterNotificationHistories.concat(notifications)
                    : notifications;

                setRemindMeLaterNotificationHistories(total);
                setGroupedRemindMeLaterNotificationHistory(groupNotificationHistory(total));
                setCurrentRemindMeLaterStart(currentRemindMeLaterStart + NOTIFICATION_COUNT);
            }
            if (notifications.length < NOTIFICATION_COUNT) {
                setHasMoreRemindMeLaterNotifications(false);
            }
        } else {
            if (notifications.length > 0) {
                const total = !isUndefined(start) ? notificationHistories.concat(notifications) : notifications;

                setNotificationHistories(total);
                setGroupedNotificationHistory(groupNotificationHistory(total));
                setCurrentStart(currentStart + NOTIFICATION_COUNT);
            }
            if (notifications.length < NOTIFICATION_COUNT) {
                setHasMoreNotifications(false);
            }
        }
    };

    // Replace notificationCount prop usage with dedicated state
    const [allNotificationCount, setAllNotificationCount] = useState<number>(notificationCount);

    // Make status optional — no status = total count
    const fetchNotificationsCount = useCallback(
        (notificationStatus?: NotificationHistoryStatus): void => {
            const search: SearchRequest = {
                filters: [
                    {
                        name: FieldName.NotificationHistoryUserID,
                        values: [String(profile?.user?.id)],
                    },
                    {
                        name: FieldName.NotificationHistoryChannel,
                        values: [NotificationChannel[NotificationChannel.InApp]],
                    },
                ],
            };

            if (notificationStatus === NotificationHistoryStatus.UnRead) {
                search.filters.push({
                    name: FieldName.NotificationHistoryStatus,
                    values: [String(NotificationHistoryStatus.UnRead)],
                });
            }

            if (notificationStatus === undefined || notificationStatus === NotificationHistoryStatus.UnRead) {
                search.filters.push({
                    name: FieldName.NotificationHistoryType,
                    values: [NotificationType[NotificationType.DataCollectionStatus]],
                    operator: Operator.NotEqual,
                });
            }

            if (notificationStatus === NotificationHistoryStatus.RemindMeLater) {
                search.filters.push({
                    name: FieldName.NotificationHistoryRemindedFlag,
                    values: ['true'],
                });
            }

            (async (): Promise<void> => {
                try {
                    if (isAuthorized({ actions: [ActionId.PlatformNotifications.View] })) {
                        const result = await trackPromise(
                            countNotificationsHistories(profile?.tenant?.id, search),
                            'notifications-history',
                        );

                        if (notificationStatus === NotificationHistoryStatus.UnRead) {
                            setUnreadNotificationCount(result.count);
                        } else if (notificationStatus === NotificationHistoryStatus.RemindMeLater) {
                            setRemindMeLaterNotificationCount(result.count);
                        } else {
                            setAllNotificationCount(result.count);
                        }
                    }
                } catch {
                    // Silently handle
                }
            })();
        },
        [profile],
    );

    const handleMarkAllRead = (): void => {
        (async (): Promise<void> => {
            try {
                await trackPromise(
                    updateNotificationHistoryStatuses(profile?.tenant?.id, {
                        userId: String(profile?.user?.id),
                        status: NotificationHistoryStatus.Read,
                    }),
                    'notifications-history',
                );
                setUnreadNotificationCount(0);
                if (notificationHistories.length === 0) {
                    handleClearUnreadNotifications();
                }
            } catch {
                // Silently handle
            }
        })();
    };

    const handleClearNotifications = (): void => {
        setNotificationHistories([]);
        setGroupedNotificationHistory({});
    };

    const handleClearUnreadNotifications = (): void => {
        setUnreadNotificationHistories([]);
        setGroupedUnreadNotificationHistory({});
    };

    const handleClearRemindMeLaterNotifications = (): void => {
        setRemindMeLaterNotificationHistories([]);
        setGroupedRemindMeLaterNotificationHistory({});
    };

    const initializeProActFlag = async (): Promise<void> => {
        if (profile?.tenant?.id) {
            const result = await isFeatureFlagEnabled(profile.tenant.id, FeatureFlagId.EnableProactNotifications);

            setIsProActFeatureFlagEnabled(result.enabled);
        }
    };

    const TYPE_TO_EVENT_TYPE: Partial<Record<number, string>> = {
        39: 'ElasticReview',
        44: 'FieldsMissingOnProfiles',
    };

    const renderActiveAlertComponent = useCallback(
        (notification: NotificationHistory): JSX.Element | null => {
            const effectiveEventType = notification.eventType || TYPE_TO_EVENT_TYPE[notification.type] || null;

            if (!effectiveEventType) {
                return null;
            }

            const loader = getDesktopAlertLoader(effectiveEventType);

            if (!loader) {
                return null;
            }

            let LazyComponent = lazyComponentCache.current.get(effectiveEventType);

            if (!LazyComponent) {
                LazyComponent = React.lazy(loader);
                lazyComponentCache.current.set(effectiveEventType, LazyComponent);
            }

            const alertData = toDesktopAlert(notification);

            return (
                <Suspense fallback={null}>
                    <LazyComponent alert={alertData} onClose={handleCloseActiveAlert} />
                </Suspense>
            );
        },
        [handleCloseActiveAlert], // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(() => {
        initializeProActFlag();
        fetchNotificationsCount();
        fetchNotificationsCount(NotificationHistoryStatus.UnRead);
        loadNotificationHistories('all');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fires AFTER the async flag resolves
    useEffect(() => {
        if (isProActFeatureFlagEnabled) {
            fetchNotificationsCount(NotificationHistoryStatus.RemindMeLater);
        }
    }, [isProActFeatureFlagEnabled, fetchNotificationsCount]);

    const activeAlertElement = useMemo(() => {
        if (!activeAlertNotification) return null;
        return renderActiveAlertComponent(activeAlertNotification);
    }, [activeAlertNotification, renderActiveAlertComponent]);

    const refreshRemindMeLaterCount = useCallback((): void => {
        if (isProActFeatureFlagEnabled) {
            fetchNotificationsCount(NotificationHistoryStatus.RemindMeLater);
        }
    }, [fetchNotificationsCount, isProActFeatureFlagEnabled]);

    return (
        <div className="r4e-notification-tray" ref={ref}>
            <Tab.Container defaultActiveKey="all">
                <Nav variant="tabs">
                    <Nav.Item>
                        <Nav.Link
                            eventKey="all"
                            onClick={(): void => {
                                loadNotificationHistories('all');
                                setCurrentStart(0);
                                setCurrentTab('all');
                                handleClearUnreadNotifications();
                            }}
                        >
                            {t('ALL')} ({allNotificationCount})
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link
                            eventKey="unread"
                            onClick={(): void => {
                                loadNotificationHistories('unread');
                                setCurrentTab('unread');
                                setCurrentUnreadStart(0);
                                handleClearNotifications();
                            }}
                        >
                            {t('UNREAD_NOTIFICATIONS')} ({unreadNotificationCount})
                        </Nav.Link>
                    </Nav.Item>
                    {isProActFeatureFlagEnabled && (
                        <Nav.Item>
                            <Nav.Link
                                eventKey="remindMeLater"
                                onClick={(): void => {
                                    loadNotificationHistories('remindMeLater');
                                    setCurrentTab('remindMeLater');
                                    setCurrentRemindMeLaterStart(0);
                                    handleClearRemindMeLaterNotifications();
                                }}
                            >
                                {t('REMIND_ME_LATER')} ({remindMeLaterNotificationCount})
                            </Nav.Link>
                        </Nav.Item>
                    )}
                    {!isProActFeatureFlagEnabled && profile && !profile.isImpersonated && unreadNotificationCount > 0 && (
                        <Button
                            variant="link"
                            className="notification-tray-mark-all-read"
                            onClick={(): void => {
                                setCurrentStart(0);
                                handleClearNotifications();
                                handleMarkAllRead();
                            }}
                        >
                            {t('MARK_ALL_READ')}
                        </Button>
                    )}
                </Nav>
                <Tab.Content>
                    <Tab.Pane transition={false} eventKey="all">
                        <Overlay area="notifications-history" />
                        {isProActFeatureFlagEnabled ? (
                            <NotificationTrayItemProAct
                                tab="all"
                                handleStatePath={handleStatePath}
                                groupedNotificationHistory={groupedNotificationHistory}
                                fetchAllNotificationHistories={loadNotificationHistories}
                                currentStart={currentStart}
                                hasMoreNotifications={hasMoreNotifications}
                                handleNotificationCount={handleNotificationCount}
                                handleNotificationAction={handleNotificationAction}
                                handleRemoveNotification={handleRemoveNotification}
                                refreshRemindMeLaterCount={refreshRemindMeLaterCount}
                            />
                        ) : (
                            <NotificationTrayItem
                                tab="all"
                                handleStatePath={handleStatePath}
                                groupedNotificationHistory={groupedNotificationHistory}
                                fetchAllNotificationHistories={loadNotificationHistories}
                                currentStart={currentStart}
                                hasMoreNotifications={hasMoreNotifications}
                                handleNotificationCount={handleNotificationCount}
                            />
                        )}
                    </Tab.Pane>
                    <Tab.Pane transition={false} eventKey="unread">
                        <Overlay area="notifications-history" />
                        {isProActFeatureFlagEnabled ? (
                            <NotificationTrayItemProAct
                                tab="unread"
                                handleStatePath={handleStatePath}
                                groupedNotificationHistory={groupedUnreadNotificationHistory}
                                fetchAllNotificationHistories={loadNotificationHistories}
                                currentStart={currentUnreadStart}
                                hasMoreNotifications={hasMoreUnreadNotifications}
                                handleNotificationAction={handleNotificationAction}
                                handleRemoveNotification={handleRemoveNotification}
                                refreshRemindMeLaterCount={refreshRemindMeLaterCount}
                            />
                        ) : (
                            <NotificationTrayItem
                                tab="unread"
                                handleStatePath={handleStatePath}
                                groupedNotificationHistory={groupedUnreadNotificationHistory}
                                fetchAllNotificationHistories={loadNotificationHistories}
                                currentStart={currentUnreadStart}
                                hasMoreNotifications={hasMoreUnreadNotifications}
                            />
                        )}
                    </Tab.Pane>
                    {isProActFeatureFlagEnabled && (
                        <Tab.Pane transition={false} eventKey="remindMeLater">
                            <Overlay area="notifications-history" />
                            <NotificationTrayItemProAct
                                tab="remindMeLater"
                                handleStatePath={handleStatePath}
                                groupedNotificationHistory={groupedRemindMeLaterNotificationHistory}
                                fetchAllNotificationHistories={loadNotificationHistories}
                                currentStart={currentRemindMeLaterStart}
                                hasMoreNotifications={hasMoreRemindMeLaterNotifications}
                                handleNotificationAction={handleNotificationAction}
                                handleRemoveNotification={handleRemoveNotification}
                                refreshRemindMeLaterCount={refreshRemindMeLaterCount}
                            />
                        </Tab.Pane>
                    )}
                </Tab.Content>
            </Tab.Container>

            {activeAlertElement}
        </div>
    );
};

export const NotificationTrayButton: FunctionComponent = () => {
    const [{ profile }] = useProfile();
    const { t } = useTranslation('admin/notifications');
    const [showNotificationTray, setShowNotificationTray] = useState(false);
    const [notificationCount, setNotificationCount] = useState<number>(0);
    const [isProActFeatureFlagEnabled, setIsProActFeatureFlagEnabled] = useState<boolean>(false);
    const isModalOpenRef = useRef(false);

    const tooltipOffset = { modifiers: [{ name: 'offset', options: { offset: [0, 15] } }] };

    const handleShowNotificationTray = (): void => {
        if (!showNotificationTray && notificationCount > 0 && profile && !profile.isImpersonated) {
            setNotificationCount(0);
            markAllNotificationHistoriesAsViewed(profile.user.tenantID, profile.user.id);
        }
        setShowNotificationTray(!showNotificationTray);
    };

    const handleOutsideClick = useCallback((): void => {
        if (isModalOpenRef.current) {
            return;
        }
        setShowNotificationTray(false);
    }, []);

    const { fetchInitialAllNotificationCount } = useNotificationsHook();

    const fetchAndSetInitialAllNotificationCount = useCallback(() => {
        const pollCount = async (): Promise<void> => {
            const count = await fetchInitialAllNotificationCount();

            if (count !== undefined) {
                setNotificationCount(count);
            }
        };

        pollCount();
        window.setTimeout(pollCount, 120000);
    }, [fetchInitialAllNotificationCount]);

    const initializeProActFlag = async (): Promise<void> => {
        if (profile?.tenant?.id) {
            const result = await isFeatureFlagEnabled(profile.tenant.id, FeatureFlagId.EnableProactNotifications);

            setIsProActFeatureFlagEnabled(result.enabled);
        }
    };

    const openTray = useCallback((): void => {
        setShowNotificationTray(true);
    }, []);

    useEffect(() => {
        initializeProActFlag();
        if (profile && !profile.isAgencyUser) {
            fetchAndSetInitialAllNotificationCount();
        }
    }, [profile, fetchAndSetInitialAllNotificationCount]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <NotificationTrayContext.Provider value={{ openTray }}>
            <OutsideClick outsideClickHandler={handleOutsideClick} styles={{ display: 'inline-block' }}>
                <Button
                    variant="icon"
                    aria-label="Toggle Notifications Tray"
                    className="top-nav-notification-tray"
                    onClick={handleShowNotificationTray}
                >
                    <OverlayTrigger
                        placement="bottom"
                        popperConfig={tooltipOffset}
                        overlay={<Tooltip id="settings-notification">{t('NOTIFICATIONS')}</Tooltip>}
                    >
                        <div>
                            {notificationCount > 0 && (
                                <span className="top-notification-count">
                                    {notificationCount > 99 ? '99+' : notificationCount}
                                </span>
                            )}
                            <i className="icon-bell" />
                        </div>
                    </OverlayTrigger>
                </Button>

                {showNotificationTray && (
                    <Suspense fallback={null}>
                        <NotificationTray notificationCount={notificationCount} isModalOpenRef={isModalOpenRef} />
                    </Suspense>
                )}

                {isProActFeatureFlagEnabled && <DesktopAlertManager hidden={showNotificationTray} />}
            </OutsideClick>
        </NotificationTrayContext.Provider>
    );
};
